<script setup lang="ts">
import { computed } from 'vue';
import { copyToClipboard, Notify } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { mdiAccountPlusOutline, mdiLinkVariant } from '@quasar/extras/mdi-v7';

const props = defineProps<{
  classDocumentId: string;
  // Selected semester the invite targets. The parent only renders this card for an
  // ongoing or future semester (a student must never be enrolled into an ended one).
  semester: {
    documentId: string;
    name?: string;
    code?: string;
    end_date?: string;
  } | null;
}>();

const { t } = useI18n();
const router = useRouter();

const semesterLabel = computed(() => {
  const s = props.semester;
  if (!s) return '';
  return s.code ? `${s.name} (${s.code})` : s.name || '';
});

// Absolute URLs (origin + resolved href) so they work regardless of router mode / base.
const registrationLink = computed(
  () =>
    window.location.origin +
    router.resolve({
      name: 'school.register',
      query: {
        classDocumentId: props.classDocumentId,
        ...(props.semester?.documentId
          ? { semesterDocumentId: props.semester.documentId }
          : {}),
      },
    }).href,
);

const enrollmentLink = computed(
  () =>
    window.location.origin +
    router.resolve({
      name: 'school.class.join',
      query: {
        classDocumentId: props.classDocumentId,
        ...(props.semester?.documentId
          ? { semesterDocumentId: props.semester.documentId }
          : {}),
      },
    }).href,
);

async function copy(link: string, successKey: string) {
  try {
    await copyToClipboard(link);
    Notify.create({ message: t(successKey), color: 'positive' });
  } catch {
    Notify.create({ message: t('ACTION_FAILED'), color: 'negative' });
  }
}
</script>

<template>
  <q-card
    flat
    bordered
    :dark="$q.dark.isActive"
  >
    <q-card-section class="q-pb-none">
      <div class="text-subtitle2">{{ $t('SCHOOL_INVITE_LINKS') }}</div>
      <div class="text-caption text-muted">
        {{ $t('SCHOOL_INVITE_LINKS_HINT') }}
      </div>
      <div
        v-if="semesterLabel"
        class="text-caption text-primary q-mt-xs"
      >
        {{ $t('SCHOOL_INVITE_FOR_SEMESTER', { name: semesterLabel }) }}
      </div>
    </q-card-section>

    <q-card-section class="row q-col-gutter-sm">
      <div class="col-12 col-sm-6">
        <q-btn
          class="full-width"
          color="primary"
          unelevated
          :icon="mdiAccountPlusOutline"
          :label="$t('SCHOOL_COPY_REGISTRATION_LINK')"
          @click="copy(registrationLink, 'SCHOOL_REGISTRATION_LINK_COPIED')"
        />
        <div class="text-caption text-muted q-mt-xs">
          {{ $t('SCHOOL_REGISTRATION_LINK_EXPLAIN') }}
        </div>
      </div>
      <div class="col-12 col-sm-6">
        <q-btn
          class="full-width"
          color="secondary"
          text-color="dark"
          unelevated
          :icon="mdiLinkVariant"
          :label="$t('SCHOOL_COPY_ENROLLMENT_LINK')"
          @click="copy(enrollmentLink, 'SCHOOL_ENROLLMENT_LINK_COPIED')"
        />
        <div class="text-caption text-muted q-mt-xs">
          {{ $t('SCHOOL_ENROLLMENT_LINK_EXPLAIN') }}
        </div>
      </div>
    </q-card-section>
  </q-card>
</template>
